import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { BASE_URL } from "../../Config";

const Company_Agent = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [companyId, setCompanyId] = useState(null); // ✅ companyId instead of userId

  // Modal & Form State
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    address: "",
    cnic: "",
    contactNumber: "",
  });
  const [formLoading, setFormLoading] = useState(false);

  // ✅ Get companyId from session
 useEffect(() => {
  const storedUserData = sessionStorage.getItem("user");
  if (!storedUserData) {
    Swal.fire("Error", "No user data found in sessionStorage.", "error");
    return;
  }
 
  const userData = JSON.parse(storedUserData);
  const companyId = userData.companyId; // ✅ local variable
  setCompanyId(companyId); // optional, agar future me use karna hai

  const fetchAgents = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}Company_Supervisor?userId=${companyId}`, // ✅ use local variable directly
        { headers: { accept: "*/*" } }
      );
      if (response.data.responseCode === "0000") {
        setAgents(response.data.agents);
      }
    } catch (error) {
      console.error("Error fetching agents", error);
    } finally {
      setLoading(false);
    }
  };

  fetchAgents();
}, []);

  // ✅ Fetch agents based on companyId
  useEffect(() => {
    const fetchAgents = async () => {
      if (!companyId) return;
      try {
        const response = await axios.get(
          `${BASE_URL}Company_Agents?userId=${companyId}`, // ✅ updated query param
          { headers: { accept: "*/*" } }
        );
        if (response.data.responseCode === "0000") {
          setAgents(response.data.agents);
        }
      } catch (error) {
        console.error("Error fetching agents", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, [companyId]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const filteredAgents = agents.filter((agent) => {
    const isStatusMatch = statusFilter === "All" || agent.status === statusFilter;
    const isSearchMatch = agent.name.toLowerCase().includes(searchQuery.toLowerCase());
    return isStatusMatch && isSearchMatch;
  });

  const handleStatusSwitch = async (agentId, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "In-Active" : "Active";
    try {
      setAgents(
        agents.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: newStatus } : agent
        )
      );
      await axios.post(
        `${BASE_URL}Change_Status`,
        { agentId, status: newStatus },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );
    } catch (error) {
      console.error("Error changing agent status", error);
      setAgents(
        agents.map((agent) =>
          agent.agentId === agentId ? { ...agent, status: currentStatus } : agent
        )
      );
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegisterAgent = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      Swal.fire("Error", "Please fill all required fields!", "error");
      return;
    }

    setFormLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}Register_Agent`, {
        companyId, // ✅ send companyId instead of userId
        ...formData,
      }, {
        headers: { "Content-Type": "application/json", accept: "*/*" },
      });

      Swal.fire({
        title: response.data.responseCode === "0000" ? "Success" : "Error",
        text: response.data.message,
        icon: response.data.responseCode === "0000" ? "success" : "error",
      });

      if (response.data.responseCode === "0000") {
        setFormData({
          name: "",
          email: "",
          password: "",
          address: "",
          cnic: "",
          contactNumber: "",
        });
        setShowModal(false);
        // Refresh agents list
        const refreshed = await axios.get(`${BASE_URL}Company_Agents?userId=${companyId}`);
        if (refreshed.data.responseCode === "0000") setAgents(refreshed.data.agents);
      }
    } catch (error) {
      console.error("Register Agent Error:", error);
      Swal.fire("Error", "Something went wrong!", "error");
    } finally {
      setFormLoading(false);
    }
  };

  const columns = [
    { name: "Agent Name", selector: (row) => row.name, sortable: true },
    { name: "CNIC", selector: (row) => row.cnic, sortable: true },
    { name: "Contact No", selector: (row) => row.contactNo, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <label className="switch">
          <input
            type="checkbox"
            checked={row.status === "Active"}
            onChange={() => handleStatusSwitch(row.agentId, row.status)}
          />
          <span className="slider round"></span>
        </label>
      ),
    },
  ];

  if (loading) return <div className="text-center">Loading...</div>;

  return (
    <div className="m-5">
      <h2 className="text-center mb-3">Company Agents</h2>

      <div className="d-flex justify-content-between mb-3">
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          + Add Agent
        </button>
        <input
          type="text"
          placeholder="Search by agent name"
          className="form-control"
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ width: "300px" }}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredAgents}
        pagination
        highlightOnHover
        responsive
        striped
      />

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-content p-4">
            <h4 className="mb-3">Register New Agent</h4>
            <form onSubmit={handleRegisterAgent}>
              <input type="text" name="name" placeholder="Name *" className="form-control mb-2" value={formData.name} onChange={handleFormChange} required />
              <input type="email" name="email" placeholder="Email *" className="form-control mb-2" value={formData.email} onChange={handleFormChange} required />
              <input type="password" name="password" placeholder="Password *" className="form-control mb-2" value={formData.password} onChange={handleFormChange} required />
              <input type="text" name="address" placeholder="Address" className="form-control mb-2" value={formData.address} onChange={handleFormChange} />
              <input type="text" name="cnic" placeholder="CNIC" className="form-control mb-2" value={formData.cnic} onChange={handleFormChange} />
              <input type="text" name="contactNumber" placeholder="Contact Number" className="form-control mb-3" value={formData.contactNumber} onChange={handleFormChange} />

              <div className="d-flex justify-content-between">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? "Registering..." : "Register Agent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 999;
        }
        .modal-content {
          background: #fff;
          border-radius: 8px;
          width: 400px;
        }
      `}</style>
    </div>
  );
};

export default Company_Agent;